import { ComponentType } from "react";
import type { EmailBlock } from "@/types/email-blocks";
import type { BlockConfiguration } from "@/types/block-configurations";

/**
 * Props passed to every block renderer component
 */
export interface BlockRendererProps {
  block: EmailBlock;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: (blockId: string) => void;
  onHover: (blockId: string | null) => void;
  children?: React.ReactNode; // For container blocks (section, column)
}

/**
 * Complete configuration for a block type
 */
export interface BlockConfig {
  type: string; // Unique identifier (e.g., "text", "image")
  displayName: string; // User-facing name (e.g., "Text Block")
  category: "content" | "layout" | "structure" | "media" | "social";
  icon?: string; // Icon name or path
  description: string; // Brief description for tooltips
  component: ComponentType<BlockRendererProps>; // React component to render this block
  defaultConfig: BlockConfiguration; // Default configuration values
  allowChildren: boolean; // Can this block contain other blocks?
  allowedParents: string[]; // Empty array = can be root level
  allowedChildren?: string[]; // If specified, only these types can be children
  maxChildren?: number; // Maximum number of children allowed
  sortOrder: number; // Display order in palette (lower = first)
  previewImage?: string; // Preview for palette
}

/**
 * Block registry singleton
 * Manages all available block types and their configurations
 */
class BlockRegistry {
  private blocks = new Map<string, BlockConfig>();

  /**
   * Register a new block type
   * @param config - Block configuration
   * @throws Error if block type already exists
   */
  register(config: BlockConfig): void {
    if (this.blocks.has(config.type)) {
      throw new Error(`Block type "${config.type}" is already registered`);
    }

    // Validate configuration
    if (!config.type || config.type.trim() === "") {
      throw new Error("Block type cannot be empty");
    }

    if (!config.displayName || config.displayName.trim() === "") {
      throw new Error(`Block type "${config.type}" must have a displayName`);
    }

    if (!config.component) {
      throw new Error(`Block type "${config.type}" must have a component`);
    }

    // Validate parent-child relationships
    if (config.allowedParents.length > 0) {
      config.allowedParents.forEach((parentType) => {
        if (parentType && !this.blocks.has(parentType)) {
          console.warn(
            `Warning: Block type "${config.type}" references unknown parent type "${parentType}"`
          );
        }
      });
    }

    this.blocks.set(config.type, config);
  }

  /**
   * Get block configuration by type
   * @param type - Block type identifier
   * @returns Block configuration or undefined if not found
   */
  get(type: string): BlockConfig | undefined {
    return this.blocks.get(type);
  }

  /**
   * Get all registered block types
   * @returns Array of all block configurations
   */
  getAll(): BlockConfig[] {
    return Array.from(this.blocks.values());
  }

  /**
   * Get blocks by category
   * @param category - Block category to filter by
   * @returns Array of block configurations in the category, sorted by sortOrder
   */
  getByCategory(
    category: BlockConfig["category"]
  ): BlockConfig[] {
    return Array.from(this.blocks.values())
      .filter((block) => block.category === category)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Get all categories with their blocks
   * @returns Map of category to array of block configurations
   */
  getAllByCategory(): Map<
    BlockConfig["category"],
    BlockConfig[]
  > {
    const categorized = new Map<
      BlockConfig["category"],
      BlockConfig[]
    >();

    const categories: BlockConfig["category"][] = [
      "structure",
      "layout",
      "content",
      "media",
      "social",
    ];

    categories.forEach((category) => {
      categorized.set(category, this.getByCategory(category));
    });

    return categorized;
  }

  /**
   * Check if a block type exists
   * @param type - Block type identifier
   * @returns True if block type is registered
   */
  has(type: string): boolean {
    return this.blocks.has(type);
  }

  /**
   * Validate if a child block can be added to a parent
   * @param childType - Type of child block to add
   * @param parentType - Type of parent block (null for root level)
   * @returns True if the relationship is valid
   */
  canAddChild(childType: string, parentType: string | null): boolean {
    const childConfig = this.get(childType);
    if (!childConfig) {
      return false;
    }

    // Check if child allows this parent
    if (parentType === null) {
      // Root level - parent must be in allowedParents or allowedParents must be empty
      return (
        childConfig.allowedParents.length === 0 ||
        childConfig.allowedParents.includes("")
      );
    }

    const parentConfig = this.get(parentType);
    if (!parentConfig) {
      return false;
    }

    // Check if parent allows children
    if (!parentConfig.allowChildren) {
      return false;
    }

    // Check if parent has allowedChildren restriction
    if (
      parentConfig.allowedChildren &&
      !parentConfig.allowedChildren.includes(childType)
    ) {
      return false;
    }

    // Check if child allows this parent
    if (childConfig.allowedParents.length > 0) {
      return childConfig.allowedParents.includes(parentType);
    }

    return true;
  }

  /**
   * Check if a parent can accept more children
   * @param parentType - Type of parent block
   * @param currentChildCount - Current number of children
   * @returns True if more children can be added
   */
  canAcceptMoreChildren(
    parentType: string,
    currentChildCount: number
  ): boolean {
    const config = this.get(parentType);
    if (!config) {
      return false;
    }

    if (!config.allowChildren) {
      return false;
    }

    if (config.maxChildren !== undefined) {
      return currentChildCount < config.maxChildren;
    }

    return true;
  }

  /**
   * Get allowed child types for a parent
   * @param parentType - Type of parent block (null for root level)
   * @returns Array of allowed child block types
   */
  getAllowedChildren(parentType: string | null): BlockConfig[] {
    if (parentType === null) {
      // Root level - return blocks that allow empty parent or no parent restrictions
      return this.getAll().filter(
        (block) =>
          block.allowedParents.length === 0 ||
          block.allowedParents.includes("")
      );
    }

    const parentConfig = this.get(parentType);
    if (!parentConfig || !parentConfig.allowChildren) {
      return [];
    }

    // If parent has allowedChildren restriction, filter by that
    if (parentConfig.allowedChildren) {
      return parentConfig.allowedChildren
        .map((type) => this.get(type))
        .filter((config): config is BlockConfig => config !== undefined);
    }

    // Otherwise, return all blocks that allow this parent
    return this.getAll().filter((block) =>
      block.allowedParents.length === 0
        ? true
        : block.allowedParents.includes(parentType)
    );
  }

  /**
   * Clear all registered blocks (useful for testing)
   */
  clear(): void {
    this.blocks.clear();
  }

  /**
   * Get count of registered blocks
   * @returns Number of registered block types
   */
  count(): number {
    return this.blocks.size;
  }

  /**
   * Unregister a block type (useful for testing or dynamic block systems)
   * @param type - Block type to unregister
   * @returns True if block was unregistered, false if it didn't exist
   */
  unregister(type: string): boolean {
    return this.blocks.delete(type);
  }
}

// Export singleton instance
export const blockRegistry = new BlockRegistry();
